import { Grid, Typography, useEventCallback } from '@mui/material';
import { DocumentCivilStatusCitizenshipCrumbs } from 'civil-status/constants/breadcrumb';
import { UploadForm } from 'civil-status/view-models/file-extended';
import { PmiButton } from 'shell/components/shared/button';
import { useFormik } from 'formik';
import { DateTime } from 'luxon';
import { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import Breadcrumbs from 'shell/components/shared/breadcrumb';
import { Cropper } from 'shell/components/shared/cropper';
import { PmiInputFile } from 'shell/components/shared/file-input';
import { StickyDiv } from 'shell/components/shared/sticky-div';
import { __CIVIL_STATUS_API_URL__ } from 'shell/constants/environment';
import { useHttpFetch } from 'shell/hooks/use-http-fetch';
import { useHttpClient } from 'shell/hooks/use-http-fetch/stateless';
import { ResponsePromise } from 'shell/hooks/use-http-fetch/stateless/type';
import { DocumentCitizenshipList } from 'shell/models/document-citizenship';
import { HttpResponse } from 'shell/models/http-response';
import { useUserProfile } from 'shell/hooks/use-user-profile';
import { ConvertedImage } from 'shell/models/convert-image';

export default function DocumentCitizenshipUploadPage(): React.ReactElement {
  const navigate = useNavigate();
  const params = useParams();
  const { userProfile } = useUserProfile();
  const [citizenshipResponse] = useHttpFetch<
    HttpResponse<DocumentCitizenshipList>,
    DocumentCitizenshipList
  >(
    `${__CIVIL_STATUS_API_URL__}/atti-cittadinanza-sima/${params.anno}-${params.numeroRegistro}-${params.numeroAtto}-${params.parte}-${params.serie1}-${params.cognome}-${params.nome}`,
    {
      onBootstrap: true,
      options: {
        errorCatching: true,
        method: 'GET',
        loading: true,
      },
      transformer: (response) => {
        const item = response.data?.payload;
        return item;
      },
    }
  );

  const unlockDocument = useHttpClient(
    `${__CIVIL_STATUS_API_URL__}/atti-cittadinanza-sima/sblocca-atto`,
    {
      options: {
        errorCatching: true,
        method: 'PUT',
        loading: true,
      },
    }
  );

  const data = citizenshipResponse.data as DocumentCitizenshipList;

  useEffect(() => {
    return () => {
      if (data?.key) {
        unlockDocument({
          data: {
            key: data?.key,
          },
        });
      }
    };
  }, [data]);

  const [insertMode, setInsertMode] = useState(false);

  // TODO cambiare
  const uploadDocument = useHttpClient(
    `${__CIVIL_STATUS_API_URL__}/atti-cittadinanza-sima/caricamento-atto-cittadinanza`,
    {
      options: {
        errorCatching: true,
        method: 'PUT',
        loading: true,
      },
    }
  );

  const handleUpload = useCallback(
    (values: UploadForm) => {
      (
        uploadDocument({
          data: {
            attoCittadinanzaForUpload: {
              numeroRegistro: data?.key?.numeroRegistro,
              anno: data?.key?.anno,
              numeroAtto: data?.key?.numeroAtto,
              parte: data?.key?.parte,
              serie: `${data?.key?.serie1 || ' '}${data?.key?.serie2 || ''}`,
              matricola: data?.matricola,
              cognome: data.key?.cognome,
              nome: data.key?.nome,
              nomePadre: data?.nomePadre,
              idOperatore: userProfile.datiLogin?.key?.userName,
            },
            filename: values.file?.name,
            mimeType: values.file?.type,
            file: values?.rawTiff?.split(',')[1],
          },
        }) as ResponsePromise<void>
      ).then(() => {
        toast.success('Operazione eseguita con successo');
        navigate('/stato-civile/cittadinanza');
      });
    },
    [uploadDocument, data, navigate]
  );

  const formContext = useFormik<UploadForm>({
    initialValues: {
      file: undefined,
      raw: [],
      rawTiff: '',
    },
    onSubmit: handleUpload,
  });

  const convertDocument = useHttpClient<HttpResponse<ConvertedImage>>(
    `${__CIVIL_STATUS_API_URL__}/convert-image`,
    {
      options: {
        errorCatching: true,
        method: 'POST',
        loading: true,
      },
    }
  );

  const handleAddFile = useEventCallback((file: File) => {
    const reader = new FileReader();
    reader.addEventListener('load', (event) => {
      const { result } = event.target || {};
      (
        convertDocument({
          data: {
            formato: 'png',
            immagineIniziale: (result as string)?.split(',')[1],
          },
        }) as ResponsePromise<HttpResponse<ConvertedImage>>
      ).then((response) => {
        formContext.setValues({
          file,
          raw: response?.data?.payload?.immagineConvertita
            ? (response?.data?.payload?.immagineConvertita as Array<string>)?.map(
                (immagine: string) => `data:image/png;base64,${immagine}`
              )
            : [],
          rawTiff: result as string,
        });
      });
    });

    reader.readAsDataURL(file);
  });

  return (
    <>
      <Breadcrumbs items={DocumentCivilStatusCitizenshipCrumbs} base="stato-civile" />
      <Typography
        color="secondary.main"
        style={{
          fontSize: '26px',
        }}
      >
        Caricamento Atto
      </Typography>
      <form onSubmit={formContext.handleSubmit}>
        <Grid container spacing={2}>
          {insertMode ? (
            <Grid item xs={12} md={4}>
              Carica documento&nbsp;
              <PmiInputFile
                accept="image/tiff"
                multiple={false}
                file={formContext.values.file}
                showFilename
                onChange={handleAddFile}
                color="cdmBrandThird"
                tipMaxSize={300}
              />
              <div className="pmi-d-flex pmi-justify-content-end pmi-pt-4">
                <div>
                  <PmiButton
                    variant="outlined"
                    color="cdmBrand"
                    onClick={() => setInsertMode(false)}
                  >
                    Indietro
                  </PmiButton>
                </div>
                <div className="pmi-pl-3">
                  <PmiButton variant="contained" color="secondary" type="submit">
                    Carica
                  </PmiButton>
                </div>
              </div>
            </Grid>
          ) : (
            <Grid item xs={12} md={4}>
              <span>Anno: {data?.key?.anno || ''}</span>
              <span className="pmi-d-block pmi-d-md-inline pmi-pl-md-2">
                Registro: {data?.key?.numeroRegistro || ''}
              </span>
              <span className="pmi-d-block pmi-d-md-inline pmi-pl-md-2">
                Atto: {data?.key?.numeroAtto || ''}
              </span>
              <span className="pmi-d-block pmi-d-md-inline pmi-pl-md-2">
                Parte: {data?.key?.parte || ''}
              </span>
              <span className="pmi-d-block pmi-d-md-inline pmi-pl-md-2">
                Serie: {data?.key?.serie1 || ''}
                {data?.key?.serie2 || ''}
              </span>
              <Typography
                color="cdmBrandThird.main"
                style={{
                  fontSize: '26px',
                }}
              >
                Atto di cittadinanza
              </Typography>
              <div className="pmi-pt-3">
                <span>Matricola</span>
                <span className="pmi-d-block pmi-d-md-inline pmi-pl-md-2">
                  {data?.matricola || ''}
                </span>
              </div>
              <div className="pmi-pt-1">
                <span>Cognome</span>
                <span className="pmi-d-block pmi-d-md-inline pmi-pl-md-2">
                  {data?.key?.cognome || ''}
                </span>
              </div>
              <div className="pmi-pt-1">
                <span>Nome</span>
                <span className="pmi-d-block pmi-d-md-inline pmi-pl-md-2">
                  {data?.key?.nome || ''}
                </span>
              </div>
              <div className="pmi-pt-1">
                <span>Sesso</span>
                <span className="pmi-d-block pmi-d-md-inline pmi-pl-md-2">{data?.sesso || ''}</span>
              </div>
              <div className="pmi-pt-1">
                <span>Data di Nascita</span>
                <span className="pmi-d-block pmi-d-md-inline pmi-pl-md-2">
                  {data?.dataNascita
                    ? DateTime.fromISO(data?.dataNascita).toFormat('dd/MM/yyyy')
                    : ''}
                </span>
              </div>
              {/* <div className="pmi-pt-1">
                <span>Ora di Nascita</span>
                <span className="pmi-d-block pmi-d-md-inline pmi-pl-md-2">
                  {data?.oraNascita ? DateTime.fromISO(data?.oraNascita).toFormat('HH:mm:ss') : ''}
                </span>
              </div> */}
              <div className="pmi-pt-1">
                <span>Comune di Nascita</span>
                <span className="pmi-d-block pmi-d-md-inline pmi-pl-md-2">
                  {data?.comuneNascita || ''}
                </span>
              </div>
              <div className="pmi-pt-1">
                <span>Comune di Residenza</span>
                <span className="pmi-d-block pmi-d-md-inline pmi-pl-md-2">
                  {data?.comuneResidenza || ''}
                </span>
              </div>
              <div className="pmi-pt-1">
                <span>Data acquisizione Cittadinanza</span>
                <span className="pmi-d-block pmi-d-md-inline pmi-pl-md-2">
                  {data?.dataCittadinanza
                    ? DateTime.fromISO(data?.dataCittadinanza).toFormat('dd/MM/yyyy')
                    : ''}
                </span>
              </div>
              <div className="pmi-d-flex pmi-justify-content-end pmi-pt-4">
                <div>
                  <PmiButton
                    variant="outlined"
                    color="cdmBrand"
                    onClick={() => navigate('/stato-civile/cittadinanza')}
                  >
                    Indietro
                  </PmiButton>
                </div>
                <div className="pmi-pl-3">
                  <PmiButton
                    variant="contained"
                    color="secondary"
                    onClick={() => setInsertMode(true)}
                  >
                    Inserisci
                  </PmiButton>
                </div>
              </div>
            </Grid>
          )}

          <Grid item xs={12} md={8}>
            <StickyDiv>
              <Cropper
                images={formContext.values.raw || []}
                disabled={!formContext.values?.raw?.length}
              />
            </StickyDiv>
          </Grid>
        </Grid>
      </form>
    </>
  );
}

DocumentCitizenshipUploadPage.displayName = 'home page component';
